import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MediaGenerationErrorAlert } from "@/components/ui/MediaGenerationErrorAlert";

interface MediaGenerationErrorApi {
  readonly show: () => void;
  readonly close: () => void;
}

const MediaGenerationErrorContext = createContext<MediaGenerationErrorApi | null>(null);

let globalShowMediaGenerationError: MediaGenerationErrorApi["show"] | null = null;

const showMediaGenerationError = (): void => {
  globalShowMediaGenerationError?.();
};

const useMediaGenerationError = (): MediaGenerationErrorApi => {
  const ctx = useContext(MediaGenerationErrorContext);
  if (!ctx) {
    throw new Error("useMediaGenerationError must be used within MediaGenerationErrorProvider");
  }

  return ctx;
};

const MediaGenerationErrorProvider = ({ children }: { readonly children: ReactNode }) => {
  const [open, setOpen] = useState(false);

  const show = useCallback(() => {
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    globalShowMediaGenerationError = show;
    return () => {
      globalShowMediaGenerationError = null;
    };
  }, [show]);

  const api = useMemo<MediaGenerationErrorApi>(() => ({ show, close }), [show, close]);

  return (
    <MediaGenerationErrorContext.Provider value={api}>
      {children}
      {open ? <MediaGenerationErrorAlert onClose={close} /> : null}
    </MediaGenerationErrorContext.Provider>
  );
};

export { MediaGenerationErrorProvider, showMediaGenerationError, useMediaGenerationError };
